import { useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { X, Camera, Wallet, Banknote, RotateCcw, Repeat } from "lucide-react";
import { api, apiErr, inr } from "@/lib/api";

const BRAND = "#d81f26";
const REASONS = ["Damaged", "Wrong item", "Size issue", "Not as described", "Quality issue", "Other"];

export default function ReturnModal({ order, onClose, onDone }) {
  const [sel, setSel] = useState(() => Object.fromEntries((order.items || []).map((it) => [it.product_id, it.quantity])));
  const [reason, setReason] = useState("");
  const [remarks, setRemarks] = useState("");
  const [resolution, setResolution] = useState("refund");
  const [refundMethod, setRefundMethod] = useState("wallet");
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const { totalVal, selVal, isPartial } = useMemo(() => {
    const tot = (order.items || []).reduce((s, it) => s + it.price * it.quantity, 0);
    const sv = (order.items || []).reduce((s, it) => s + it.price * (sel[it.product_id] || 0), 0);
    return { totalVal: tot, selVal: sv, isPartial: sv < tot - 0.01 };
  }, [order.items, sel]);

  const toggle = (pid, qty) => setSel((s) => ({ ...s, [pid]: s[pid] > 0 ? 0 : qty }));
  const setQ = (pid, q, max) => setSel((s) => ({ ...s, [pid]: Math.max(0, Math.min(max, q)) }));

  const pickPhotos = (e) => {
    const files = Array.from(e.target.files || []);
    files.forEach((f) => {
      if (photos.length >= 4) { toast.error("Max 4 photos"); return; }
      const r = new FileReader();
      r.onload = () => setPhotos((p) => (p.length >= 4 ? p : [...p, r.result]));
      r.readAsDataURL(f);
    });
    e.target.value = "";
  };

  const submit = async () => {
    if (!reason.trim()) { toast.error("Return ka reason chunein"); return; }
    const items = Object.entries(sel).filter(([, q]) => q > 0).map(([product_id, qty]) => ({ product_id, qty }));
    if (items.length === 0) { toast.error("Kam se kam ek item chunein"); return; }
    setBusy(true);
    try {
      await api.post(`/orders/${order.id}/return-request`, {
        items, reason: reason.trim(), remarks: remarks.trim(), resolution,
        refund_method: isPartial ? "wallet" : refundMethod, photos,
      });
      toast.success("Return request bhej diya gaya");
      onDone?.();
      onClose();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/50 flex items-end sm:items-center justify-center" data-testid="return-modal" onClick={onClose}>
      <div className="w-full sm:max-w-lg bg-white rounded-t-3xl sm:rounded-3xl p-5 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="mx-auto w-11 h-1.5 rounded-full bg-slate-200 mb-4 sm:hidden" />
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-xl font-extrabold">Return / Refund</h2>
            <p className="text-xs text-slate-500 mt-0.5">Order #{order.order_no} · {inr(order.total)}</p>
          </div>
          <button data-testid="return-close" onClick={onClose} className="p-1 -mr-1 text-slate-400 hover:text-slate-700"><X size={22} /></button>
        </div>

        <p className="text-sm font-bold text-slate-600 mt-5 mb-2">Kaunse items return karne hain?</p>
        <div className="space-y-1">
          {(order.items || []).map((it) => {
            const q = sel[it.product_id] || 0;
            const on = q > 0;
            return (
              <div key={it.product_id} data-testid={`ret-item-${it.product_id}`} className="flex items-center gap-3 py-2 border-b border-slate-100">
                <button onClick={() => toggle(it.product_id, it.quantity)} className={`w-6 h-6 rounded-md border-2 flex items-center justify-center shrink-0 ${on ? "text-white" : "border-slate-300"}`} style={on ? { backgroundColor: BRAND, borderColor: BRAND } : {}}>
                  {on && <span className="text-xs font-bold">✓</span>}
                </button>
                {it.image ? <img src={it.image} alt="" className="w-10 h-10 rounded-lg object-cover shrink-0" /> : <div className="w-10 h-10 rounded-lg bg-slate-100 shrink-0" />}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold truncate">{it.name}</p>
                  <p className="text-xs text-slate-500">{inr(it.price)} × {it.quantity}</p>
                </div>
                {on && it.quantity > 1 && (
                  <div className="flex items-center gap-2 bg-slate-100 rounded-lg px-1">
                    <button onClick={() => setQ(it.product_id, q - 1, it.quantity)} className="w-7 h-7 font-bold text-slate-600">−</button>
                    <span className="w-5 text-center text-sm font-bold">{q}</span>
                    <button onClick={() => setQ(it.product_id, q + 1, it.quantity)} className="w-7 h-7 font-bold text-slate-600">+</button>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <p className="text-sm font-bold text-slate-600 mt-5 mb-2">Return ka reason</p>
        <div className="flex flex-wrap gap-2">
          {REASONS.map((r) => {
            const on = reason === r;
            return (
              <button key={r} data-testid={`reason-${r}`} onClick={() => setReason(r)} className={`px-3 py-1.5 rounded-full text-sm font-semibold border-[1.5px] ${on ? "text-white" : "border-slate-200 text-slate-600"}`} style={on ? { backgroundColor: BRAND, borderColor: BRAND } : {}}>{r}</button>
            );
          })}
        </div>
        <textarea data-testid="return-remarks" value={remarks} onChange={(e) => setRemarks(e.target.value)} placeholder="Remarks — problem detail me likhein (optional)" className="mt-3 w-full h-20 rounded-xl border border-slate-200 p-3 text-sm resize-none focus:outline-none focus:ring-2" style={{ "--tw-ring-color": BRAND }} />

        <p className="text-sm font-bold text-slate-600 mt-4 mb-2">Photos (optional)</p>
        <div className="flex flex-wrap gap-2">
          {photos.map((p, i) => (
            <div key={p} className="relative">
              <img src={p} alt="" className="w-16 h-16 rounded-lg object-cover" />
              <button data-testid={`photo-remove-${i}`} onClick={() => setPhotos((a) => a.filter((_, idx) => idx !== i))} className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-red-600 text-white flex items-center justify-center"><X size={12} /></button>
            </div>
          ))}
          {photos.length < 4 && (
            <button data-testid="return-add-photo" onClick={() => fileRef.current?.click()} className="w-16 h-16 rounded-lg border-[1.5px] border-dashed flex flex-col items-center justify-center gap-0.5" style={{ borderColor: BRAND, color: BRAND }}>
              <Camera size={20} /><span className="text-[10px] font-semibold">Add</span>
            </button>
          )}
          <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={pickPhotos} />
        </div>

        <p className="text-sm font-bold text-slate-600 mt-5 mb-2">Aapko kya chahiye?</p>
        <div className="grid grid-cols-2 gap-3">
          {[
            { k: "refund", icon: Wallet, t: "Refund", s: "Paise wapas" },
            { k: "replacement", icon: Repeat, t: "Replacement", s: "Naya product free" },
          ].map(({ k, icon: Icon, t, s }) => {
            const on = resolution === k;
            return (
              <button key={k} data-testid={`res-${k}`} onClick={() => setResolution(k)} className="border-[1.5px] rounded-xl p-3 flex flex-col items-center gap-0.5" style={on ? { borderColor: BRAND, backgroundColor: BRAND + "10" } : { borderColor: "#e2e8f0" }}>
                <Icon size={18} color={on ? BRAND : "#94a3b8"} />
                <span className="text-sm font-bold" style={{ color: on ? BRAND : "#0f172a" }}>{t}</span>
                <span className="text-[11px] text-slate-400">{s}</span>
              </button>
            );
          })}
        </div>

        {resolution === "replacement" ? (
          <div data-testid="replacement-note" className="mt-3 rounded-xl p-3 flex gap-2 text-sm" style={{ backgroundColor: BRAND + "10", color: "#7f1d1d" }}>
            <Repeat size={18} color={BRAND} className="shrink-0" />
            Purana product wapas hone ke baad same product ka naya piece FREE deliver hoga — koi refund nahi katega.
          </div>
        ) : (
          <>
            <p className="text-sm font-bold text-slate-600 mt-4 mb-2">Refund kahan chahiye?</p>
            {isPartial ? (
              <div data-testid="refund-locked" className="rounded-xl p-3 flex items-center gap-2 text-sm" style={{ backgroundColor: BRAND + "10" }}>
                <Wallet size={18} color={BRAND} />
                <span className="font-bold" style={{ color: BRAND }}>App Wallet</span>
                <span className="text-[11px] text-slate-500">Partial return par wallet me hi credit hota hai</span>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                {[
                  { k: "wallet", icon: Wallet, t: "App Wallet", s: "Instant credit" },
                  { k: "bank", icon: Banknote, t: "Bank / UPI", s: "Manual refund" },
                ].map(({ k, icon: Icon, t, s }) => {
                  const on = refundMethod === k;
                  return (
                    <button key={k} data-testid={`refund-${k}`} onClick={() => setRefundMethod(k)} className="border-[1.5px] rounded-xl p-3 flex flex-col items-center gap-0.5" style={on ? { borderColor: BRAND, backgroundColor: BRAND + "10" } : { borderColor: "#e2e8f0" }}>
                      <Icon size={18} color={on ? BRAND : "#94a3b8"} />
                      <span className="text-sm font-bold" style={{ color: on ? BRAND : "#0f172a" }}>{t}</span>
                      <span className="text-[11px] text-slate-400">{s}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </>
        )}

        <div className="flex gap-3 mt-6">
          <button data-testid="return-cancel" onClick={onClose} className="flex-1 h-12 rounded-xl border border-slate-200 font-bold text-slate-700">Cancel</button>
          <button data-testid="return-submit" onClick={submit} disabled={busy} className="flex-1 h-12 rounded-xl text-white font-bold disabled:opacity-60" style={{ backgroundColor: BRAND }}>{busy ? "…" : "Submit Return"}</button>
        </div>
      </div>
    </div>
  );
}
